import { useState, type ChangeEvent, type FormEvent } from 'react';
import { useToast } from '@/features/ui/toast/useToast';
import { IconX } from './icons';

export type CustomerFormValues = {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  company: string;
  address: string;
  notes: string;
  purchase_count: number;
  nps_score: number | '';
};

const empty: CustomerFormValues = {
  first_name: '',
  last_name: '',
  email: '',
  phone: '',
  company: '',
  address: '',
  notes: '',
  purchase_count: 1,
  nps_score: '',
};

type Props = {
  onClose: () => void;
  onSave: (values: CustomerFormValues) => Promise<string | null>;
};

export function CustomerForm({ onClose, onSave }: Props) {
  const toast = useToast();
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const set = (key: keyof CustomerFormValues) => (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
    setForm({ ...form, [key]: e.target.value });

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSaving(true);
    const err = await onSave(form);
    setSaving(false);
    if (err) toast.error(err);
    else onClose();
  }

  return (
    <div className="crm-modal-backdrop" role="presentation" onClick={onClose}>
      <div className="crm-modal crm-modal--sheet" role="dialog" aria-labelledby="crm-customer-form-title" onClick={(e) => e.stopPropagation()}>
        <div className="crm-modal-sticky">
          <h2 id="crm-customer-form-title">Nouveau client</h2>
          <button type="button" className="crm-modal-x" onClick={onClose} aria-label="Fermer">
            <IconX />
          </button>
        </div>
        <form onSubmit={(e) => void submit(e)} className="crm-modal-body">
          <div className="grid-2">
            <div className="field">
              <label>Prénom *</label>
              <input required value={form.first_name} onChange={set('first_name')} />
            </div>
            <div className="field">
              <label>Nom *</label>
              <input required value={form.last_name} onChange={set('last_name')} />
            </div>
            <div className="field">
              <label>Email</label>
              <input type="email" value={form.email} onChange={set('email')} />
            </div>
            <div className="field">
              <label>Téléphone</label>
              <input value={form.phone} onChange={set('phone')} />
            </div>
          </div>
          <div className="field">
            <label>Entreprise</label>
            <input value={form.company} onChange={set('company')} />
          </div>
          <div className="field">
            <label>Adresse</label>
            <input value={form.address} onChange={set('address')} />
          </div>
          <div className="grid-2">
            <div className="field">
              <label>Nombre d’achats</label>
              <input
                type="number"
                min={0}
                value={form.purchase_count}
                onChange={(e) => setForm({ ...form, purchase_count: Number(e.target.value) })}
              />
            </div>
            <div className="field">
              <label>Score NPS (0–10)</label>
              <input
                type="number"
                min={0}
                max={10}
                value={form.nps_score}
                onChange={(e) =>
                  setForm({
                    ...form,
                    nps_score: e.target.value === '' ? '' : Number(e.target.value),
                  })
                }
              />
            </div>
          </div>
          <div className="field">
            <label>Notes</label>
            <textarea rows={3} value={form.notes} onChange={set('notes')} />
          </div>
          <div className="crm-modal-actions crm-modal-actions--split">
            <button type="button" className="btn crm-btn-outline" onClick={onClose}>
              Annuler
            </button>
            <button className="btn btn-primary" type="submit" disabled={saving}>
              {saving ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
